import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api.js';
import AgentMark from '../components/AgentMark.jsx';
import FigureBlock from '../components/FigureBlock.jsx';
import { AGENTS } from '../agents.js';

export default function Dossier() {
  const { dossierId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [d, setD] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api(`/api/dossiers/${dossierId}`).then(setD).catch(() => setD(false));
  }, [dossierId]);

  if (d === false) return <div style={{ padding: 40 }}>Not found.</div>;
  if (!d) return null;

  const agent = AGENTS[d.agent] || { name: d.agent, glyph: d.agent?.slice(0, 3).toUpperCase(), mark: 'var(--accent)', role: '' };

  // Back navigation — prefer history state, fall back to projects root
  const fromProject = location.state?.projectId;
  const backLabel = location.state?.projectTitle || 'Projects';
  const backPath = fromProject ? `/project/${fromProject}` : '/';

  const renderParagraph = (para, key) => {
    if (typeof para === 'string') return <p key={key}>{para}</p>;
    const cites = (para.cites || []);
    return (
      <p key={key}>
        {para.t}
        {cites.length > 0 && (
          <span className="cite-marker">[{cites.join(',')}]</span>
        )}
      </p>
    );
  };

  return (
    <div className="dossier">
      <button className="dossier-back" onClick={() => nav(backPath)}>
        ← {backLabel}
      </button>

      {/* COVER RENDER */}
      {d.cover?.src && (
        <div className="dossier-cover">
          <img src={d.cover.src} alt={d.cover.prompt || d.title} />
          <span className="label-strip">
            <span className="dot" />
            {(d.cover.kind || 'RENDER').toUpperCase()}
            <span className="by">· {agent.name.toUpperCase()}</span>
          </span>
          {d.cover.prompt && (
            <div className="prompt-strip">
              <strong>Prompt</strong>
              "{d.cover.prompt}"
            </div>
          )}
        </div>
      )}

      {/* HEADER */}
      <header className="dossier-h">
        <div className="dossier-meta">
          <span>DOSSIER</span>
          <span className="sep">·</span>
          <span className="agent-info">
            <AgentMark agent={agent} size="xs" />
            <span className="name">{agent.name.toUpperCase()}</span>
          </span>
          <span className="sep">·</span>
          <span>{d.date ? d.date.toUpperCase() : ''}</span>
          {d.duration && (
            <>
              <span className="sep">·</span>
              <span>{d.duration.toUpperCase()}</span>
            </>
          )}
          <span className="sep">·</span>
          <span>VERSION {d.version}{d.current ? ' · CURRENT' : ''}</span>
          {d.citations?.length > 0 && (
            <span className="tag" style={{ marginLeft: 'auto' }}>{d.citations.length} CITATIONS</span>
          )}
        </div>
        <h1>{d.title}</h1>
        {d.standfirst && <p className="dossier-standfirst">"{d.standfirst}"</p>}
        <div className="dossier-actions">
          {d.threadId && (
            <button className="btn accent" onClick={() => nav(`/thread/${d.threadId}`)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              Respond to {agent.name}
            </button>
          )}
          <button className="btn ghost">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" />
            </svg>
            Re-run with notes
          </button>
          <button className="btn ghost">Pin to memory</button>
        </div>
      </header>

      {/* BODY — 3 columns */}
      <div className="dossier-body">

        {/* LEFT: TOC + Versions */}
        <aside className="dossier-toc">
          {d.toc?.length > 0 && (
            <>
              <h4>In this dossier</h4>
              <ol>
                {d.toc.map((t, i) => (
                  <li
                    key={i}
                    className={i === active ? 'is-active' : ''}
                    onClick={() => setActive(i)}
                  >
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </>
          )}

          {d.versions?.length > 0 && (
            <div className="dossier-versions">
              <h4>Versions</h4>
              {d.versions.slice().reverse().map((v, i) => (
                <div key={i} className={`dossier-version${v.current ? ' is-current' : ''}`}>
                  <span className="v">v.{v.v}{v.current ? ' · current' : ''}</span>
                  <span className="d">{v.date}</span>
                  {v.note && <div style={{ marginTop: 4, color: 'var(--ink-3)' }}>{v.note}</div>}
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* CENTRE: Prose + Figures */}
        <article className="dossier-prose">
          {d.sections?.map((s, i) => (
            <section key={i}>
              <h2>{s.heading}</h2>
              {(s.prose || []).map((p, j) => (
                <React.Fragment key={j}>
                  {renderParagraph(p, j)}
                  {j === 0 && s.pullQuote && (
                    <div className="pullquote">"{s.pullQuote}"</div>
                  )}
                </React.Fragment>
              ))}

              {/* Inline figures attached to this section */}
              {(s.figures || []).map((fig, k) => (
                <FigureBlock
                  key={k}
                  num={`${i + 1}.${k + 1}`}
                  title={fig.title}
                  kind={fig.kind || 'Render'}
                  agent={fig.agent || agent.name}
                  prompt={fig.prompt}
                >
                  {fig.src
                    ? <img src={fig.src} alt={fig.title} style={{ width: '100%', display: 'block' }} />
                    : <div style={{ height: 160, background: 'var(--panel-2)' }} />
                  }
                </FigureBlock>
              ))}
            </section>
          ))}

          {/* Tail — remaining TOC items not yet rendered */}
          {d.toc?.length > (d.sections?.length || 0) && (
            <section style={{ borderTop: '1px solid var(--rule)', paddingTop: 24 }}>
              <h2>The rest of this dossier</h2>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {d.toc.slice(d.sections?.length || 0).map((t, i) => (
                  <li
                    key={i}
                    style={{
                      padding: '14px 0',
                      borderTop: '1px solid var(--rule-3)',
                      fontFamily: 'var(--font-sans)',
                      fontStyle: 'italic',
                      fontSize: 18,
                      color: 'var(--ink-2)',
                    }}
                  >
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontStyle: 'normal',
                      color: 'var(--ink-4)',
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      marginRight: 12,
                    }}>
                      {String((d.sections?.length || 0) + i + 1).padStart(2, '0')}
                    </span>
                    {t}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </article>

        {/* RIGHT: Citations + Consequences */}
        <aside className="dossier-rail">
          {d.citations?.length > 0 && (
            <>
              <h4>Citations · {d.citations.length}</h4>
              {d.citations.map((c, i) => (
                <div key={i} id={`cite-${c.n}`} className="cite">
                  <div>
                    <span className="n">[{c.n}]</span>
                    <span className="title">{c.title}</span>
                  </div>
                  <div className="kind">{c.kind}</div>
                  <div className="who">{c.author}{c.year ? ` · ${c.year}` : ''}</div>
                  {c.url && (
                    <a className="url" href={c.url} target="_blank" rel="noreferrer">
                      {c.url.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {c.annotation && <div className="annot">"{c.annotation}"</div>}
                </div>
              ))}
            </>
          )}

          {d.consequences?.length > 0 && (
            <>
              <h4 style={{ marginTop: 32 }}>What this produced</h4>
              {d.consequences.map((c, i) => (
                <div key={i} className="consq">
                  <div className="kind">{c.kind}</div>
                  <div className="ref">{c.ref}</div>
                </div>
              ))}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
